import { useEffect, useRef, useState } from 'react';

/**
 * The Shoot View framed inside a call sheet (mock-ups C4 and C5).
 *
 * Presentation hint: the call sheet opens either Shoot View route with
 * `?embed=callsheet`. The page then drops its own header, because the call
 * sheet draws a slim bar (back, doc type, open in a new tab) above the frame.
 * The hint is read once when the page mounts and is presentation only: it is
 * never trusted for access, audience or anything else security related. The
 * session and the API decide what the page shows.
 *
 * Message contract (consumed by the call sheet, WP5b):
 *
 *   window.parent.postMessage(
 *     { source: 'ss-scripts', type: 'shoot-recording', state: 'started' | 'stopped' },
 *     <the parent's exact origin>,
 *   )
 *
 *   - 'started': a take has begun (admin brief recorder only; scripts have
 *     no recorder).
 *   - 'stopped': the take has stopped AND its upload has finished or failed.
 *     An upload in flight still counts as recording, so the call sheet should
 *     keep guarding its close button until 'stopped' arrives.
 *   - A 'started' is always followed by one 'stopped', including when the
 *     Shoot View unmounts mid-take.
 *   - Nothing is posted unless the hint is present, the page is actually
 *     framed (window.parent !== window) and the parent's origin is on the
 *     allowlist. The target origin is always explicit, never '*'.
 *
 * Parent origin: `location.ancestorOrigins[0]` where the browser has it,
 * otherwise the origin of `document.referrer`. Allowed parents:
 * https://callsheets.sheahanstudios.com, plus, only while this page is itself
 * served from localhost or 127.0.0.1, any http://localhost:<port> or
 * http://127.0.0.1:<port> for local testing.
 */

export const CALLSHEET_ORIGIN = 'https://callsheets.sheahanstudios.com';

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

export type RecordingState = 'started' | 'stopped';

export interface RecordingMessage {
  source: 'ss-scripts';
  type: 'shoot-recording';
  state: RecordingState;
}

export function isCallsheetEmbed(search: string): boolean {
  return new URLSearchParams(search).get('embed') === 'callsheet';
}

/** The hint as the page found it on mount. Later URL changes never flip it. */
export function useCallsheetEmbed(): boolean {
  const [embedded] = useState(() =>
    typeof window === 'undefined' ? false : isCallsheetEmbed(window.location.search),
  );
  return embedded;
}

function originOf(value: string | undefined | null): string | null {
  if (!value) return null;
  try {
    const origin = new URL(value).origin;
    return origin === 'null' ? null : origin;
  } catch {
    return null;
  }
}

/** Where the frame's parent lives, as far as the browser will say. */
export function resolveParentOrigin(
  ancestorOrigins: ArrayLike<string> | undefined | null,
  referrer: string,
): string | null {
  const ancestor = ancestorOrigins && ancestorOrigins.length > 0 ? ancestorOrigins[0] : null;
  return ancestor ? originOf(ancestor) : originOf(referrer);
}

export function isAllowedParentOrigin(origin: string | null, selfHostname: string): boolean {
  if (!origin) return false;
  if (origin === CALLSHEET_ORIGIN) return true;
  if (!LOCAL_HOSTS.has(selfHostname)) return false;
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  // An origin string only: no path, credentials or trailing slash smuggled in.
  if (url.origin !== origin) return false;
  return url.protocol === 'http:' && LOCAL_HOSTS.has(url.hostname) && url.port !== '';
}

/** The allowlisted parent origin to post to, or null to post nothing. */
export function callsheetParentOrigin(): string | null {
  if (typeof window === 'undefined' || window.parent === window) return null;
  const origin = resolveParentOrigin(window.location.ancestorOrigins, document.referrer);
  return isAllowedParentOrigin(origin, window.location.hostname) ? origin : null;
}

function postRecordingState(state: RecordingState): boolean {
  const origin = callsheetParentOrigin();
  if (!origin) return false;
  const message: RecordingMessage = { source: 'ss-scripts', type: 'shoot-recording', state };
  try {
    window.parent.postMessage(message, origin);
    return true;
  } catch {
    return false;
  }
}

// Between Stop and the upload starting the recorder passes through idle for a
// render or two; a short grace keeps that gap from reading as a finished take.
const STOP_GRACE_MS = 400;

/**
 * Tells the call sheet while a take is in progress. `inProgress` covers the
 * whole take: recording, stopping and uploading.
 */
export function useCallsheetRecordingSignal(embedded: boolean, inProgress: boolean): void {
  const started = useRef(false);
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!embedded) return;
    if (inProgress) {
      if (stopTimer.current) {
        clearTimeout(stopTimer.current);
        stopTimer.current = null;
      }
      if (!started.current) started.current = postRecordingState('started');
      return;
    }
    if (!started.current || stopTimer.current) return;
    stopTimer.current = setTimeout(() => {
      stopTimer.current = null;
      started.current = false;
      postRecordingState('stopped');
    }, STOP_GRACE_MS);
  }, [embedded, inProgress]);

  useEffect(
    () => () => {
      if (stopTimer.current) clearTimeout(stopTimer.current);
      if (started.current) {
        started.current = false;
        postRecordingState('stopped');
      }
    },
    [],
  );
}
